import styled from 'styled-components';
import { brandTheme } from '../../../theme/brandTokens';

/**
 * Single-offer page — visual port of the Remix reference's
 * `routes/offers.$offerId.tsx` (and `offers.new.tsx`, which shares the shell).
 *
 * Same conventions as Offers.styles.js: --hd-* tokens only, and the
 * reference's overridden radius scale (--radius: 0.875rem):
 *   sm 10 · md 12 · lg 14 · xl 18 · 2xl 22 · full 999
 *
 * Careful: this whole file is one big template literal per component. A
 * backtick inside a CSS comment ends the template early and the module throws
 * on load — keep comments backtick-free.
 */

const FG = 'var(--hd-foreground)';
const MUTED_FG = 'var(--hd-muted-foreground)';
const MUTED = 'var(--hd-muted)';
const BORDER = 'var(--hd-border)';
const BG = 'var(--hd-background)';
const DESTRUCTIVE = 'var(--hd-destructive)';

const MUTED_30 = 'color-mix(in srgb, var(--hd-muted) 30%, transparent)';
const DESTRUCTIVE_10 = 'color-mix(in srgb, var(--hd-destructive) 10%, transparent)';

export const StyledOfferDetail = styled.div`
  ${brandTheme}
  min-height: 100%;
  background: ${BG};
  color: ${FG};
  /* Same stack the reference sets on html/body via --font-sans. Sizes below use
     Tailwind's paired line heights (xs 12/16, sm 14/20, base 16/24, 3xl 30/36),
     which is what the reference's utilities resolve to. */
  font-family: var(--base-font-sans-serif);
  -webkit-font-smoothing: antialiased;

  /* Reference: mx-auto max-w-[820px] px-8 py-10 */
  .detail-main {
    width: 100%;
    max-width: 820px;
    margin: 0 auto;
    padding: 32px;

    @media (max-width: 767px) {
      padding: 32px 16px;
    }
  }

  .detail-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    border: none;
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    color: ${MUTED_FG};
    background: transparent;
    cursor: pointer;
    transition: color 0.15s ease;

    &:hover {
      color: ${FG};
    }
  }

  /* The back control and its unsaved-changes prompt share a stacking context so
     the prompt can anchor under the link. */
  .detail-back-wrap {
    position: relative;
    display: inline-block;
  }

  .leave-confirm {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    z-index: 5;
    width: 260px;
    padding: 12px;
    border: 1px solid ${BORDER};
    border-radius: 18px;
    background: var(--hd-popover);
    box-shadow: var(--hd-shadow-elevated);
    text-align: left;

    p {
      margin: 0;
      font-size: 13px;
      line-height: 1.5;
      color: ${FG};
    }

    .confirm-actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 12px;
    }

    button {
      height: 30px;
      padding: 0 10px;
      border: 1px solid ${BORDER};
      border-radius: 10px;
      font-family: inherit;
      font-size: 13px;
      font-weight: 500;
      color: ${FG};
      background: ${BG};
      cursor: pointer;

      &.danger {
        border-color: transparent;
        color: #fff;
        background: ${DESTRUCTIVE};
      }
    }
  }

  /* Reference: mt-4 flex items-start justify-between gap-4 */
  .detail-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-top: 16px;

    @media (max-width: 639px) {
      flex-direction: column;
    }
  }

  .detail-identity {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    min-width: 0;
  }

  .detail-icon {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 48px;
    height: 48px;
    border-radius: 18px;
    color: ${FG};
    background: ${MUTED};
  }

  .detail-title {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
    letter-spacing: -0.025em;
    /* GlobalStyles capitalises every h1 — the reference does not. */
    text-transform: none;
    color: ${FG};
  }

  .detail-tagline {
    margin: 4px 0 0;
    font-size: 16px;
    line-height: 24px;
    color: ${MUTED_FG};
  }

  .detail-actions {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 8px;
  }

  /* Reference: h-9 rounded-lg border border-border bg-background px-3 */
  .detail-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 12px;
    border: 1px solid ${BORDER};
    border-radius: 14px;
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: ${FG};
    background: ${BG};
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover:not(:disabled) {
      background: ${MUTED};
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &.danger {
      color: ${DESTRUCTIVE};

      &:hover:not(:disabled) {
        background: ${DESTRUCTIVE_10};
      }
    }
  }

  /* Reference: mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 */
  .detail-info-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 32px;

    @media (min-width: 640px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .detail-info-card {
    padding: 16px;
    border: 1px solid ${BORDER};
    border-radius: 22px;
    background: ${BG};

    .info-label {
      font-size: 12px;
      line-height: 16px;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: ${MUTED_FG};
    }

    .info-value {
      margin-top: 4px;
      font-size: 14px;
      line-height: 20px;
      font-weight: 500;
      word-break: break-word;
      color: ${FG};
    }
  }

  .detail-updated {
    margin-top: 24px;
    font-size: 12px;
    line-height: 16px;
    color: ${MUTED_FG};
  }

  .detail-missing {
    margin-top: 40px;
    padding: 40px;
    border: 1px solid ${BORDER};
    border-radius: 22px;
    text-align: center;
    background: ${MUTED_30};

    .missing-title {
      font-size: 14px;
      line-height: 20px;
      font-weight: 500;
      color: ${FG};
    }

    p {
      margin: 4px 0 0;
      font-size: 14px;
      line-height: 20px;
      color: ${MUTED_FG};
    }
  }

  /* Edit mode swaps the read view for the offer form, the way the reference
     swaps in its OfferForm. */
  .detail-edit-title {
    margin: 16px 0 0;
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
    letter-spacing: -0.025em;
    text-transform: none;
    color: ${FG};
  }

  .detail-edit-lead {
    margin: 4px 0 0;
    font-size: 14px;
    line-height: 20px;
    color: ${MUTED_FG};
  }

`;

/**
 * The offer form (CreateCompaign/AICampaign) restyled to the reference's
 * OfferForm: bordered cards, its .input rule on every control, and a
 * right-aligned Cancel / Save footer.
 *
 * Only presentation is overridden here — the form keeps every field, rule and
 * handler it already had, so this stays a skin over the existing component
 * rather than a fork of it.
 */
export const StyledOfferFormShell = styled.div`
  margin-top: 32px;
  font-family: var(--base-font-sans-serif);

  --offer-primary-soft: color-mix(in oklab, var(--hd-primary) 8%, transparent);
  --offer-primary-line: color-mix(in oklab, var(--hd-primary) 35%, transparent);
  --offer-focus: color-mix(in srgb, var(--hd-foreground) 30%, transparent);

  /* Form controls do not inherit the page font by default. */
  input,
  textarea,
  select,
  button {
    font-family: inherit;
  }

  /*
   * Every rule below is scoped under .content-holder (and .fields-wrapper for
   * the fields), the same chain AICampaign styles itself with, so these win
   * over its originals without !important and without touching the campaign
   * builder, which renders the same form outside this shell.
   */
  .content-holder {
    max-width: 100%;
  }

  /* version3 OfferForm: rounded-2xl border bg-background p-6 shadow-card. The
     form here is grouped, so each group is one such card. */
  .content-holder .fields-wrapper .input-box {
    margin: 0 0 16px;
    padding: 24px;
    border: 1px solid ${BORDER};
    border-radius: 22px;
    background: ${BG};
    box-shadow: var(--hd-shadow-card);
  }

  /* A box inside a card is a row of it (a feature pair, a personalization
     option), not another card. */
  .content-holder .fields-wrapper .input-box .input-box {
    margin: 0 0 12px;
    padding: 16px;
    border-radius: 18px;
    background: ${MUTED_30};
    box-shadow: none;
  }

  .content-holder .fields-wrapper .input-flex {
    gap: 16px;
  }

  .content-holder .fields-wrapper .section-heading {
    margin: 0 0 14px;
    font-size: 15px;
    line-height: 22px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: ${FG};

    .sub-heading {
      margin: 0;
      font-size: 13px;
      font-weight: 400;
      color: ${MUTED_FG};
    }
  }

  .content-holder .fields-wrapper label {
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: ${FG};
  }

  /* version3's .input: rounded 10px, 10px 12px padding, sm text. Not on
     react-select's own search input: given this height and padding it made
     the dropdown's inside taller than its box, so Tone and Language read low. */
  .content-holder .fields-wrapper input:not([type='checkbox']):not([type='radio']):not(.react-select__input),
  .content-holder .fields-wrapper textarea {
    width: 100%;
    height: auto;
    min-height: 42px;
    padding: 10px 12px;
    border: 1px solid ${BORDER};
    border-radius: 10px;
    font-size: 14px;
    line-height: 20px;
    color: ${FG};
    background: ${BG};
    box-shadow: none;
    outline: none;
    transition:
      border-color 120ms ease,
      box-shadow 120ms ease;

    &::placeholder {
      color: ${MUTED_FG};
    }

    &:focus {
      border-color: var(--offer-focus);
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--hd-foreground) 5%, transparent);
    }
  }

  .content-holder .fields-wrapper textarea {
    min-height: 100px;
    resize: none;
  }

  /* react-select draws its own control; give it the same shape. */
  .content-holder .fields-wrapper .react-select__control {
    min-height: 42px;
    /* The shared Select pads 20px; 10px plus react-select's 2px text margin
       starts the value 13px in, level with the text in every other field. */
    padding-left: 10px;
    border-color: ${BORDER};
    border-radius: 10px;
    background: ${BG};
    box-shadow: none;

    &--is-focused {
      border-color: var(--offer-focus);
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--hd-foreground) 5%, transparent);
    }
  }

  .content-holder .fields-wrapper .react-select__menu {
    overflow: hidden;
    border: 1px solid ${BORDER};
    border-radius: 14px;
    box-shadow: var(--hd-shadow-card);
  }

  .content-holder .fields-wrapper .react-select__option {
    font-size: 14px;

    &--is-focused {
      background: ${MUTED};
    }

    &--is-selected {
      color: var(--hd-primary);
      background: var(--offer-primary-soft);
    }
  }

  /* "Auto-fill by AI": the brand gradient, full width. */
  .content-holder .fields-wrapper .auto-fill-btn {
    height: 44px;
    margin: 0 0 16px;
    padding: 0 20px;
    border: none;
    border-radius: 14px;
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    background: var(--hd-gradient-houdiny);
    box-shadow: var(--hd-shadow-card);
    transition:
      transform 0.15s ease,
      opacity 0.15s ease;

    &:hover:not(:disabled) {
      transform: scale(1.01);
    }

    &:disabled {
      opacity: 0.55;
    }
  }

  /* The "+ Add …" buttons under each list: a soft primary pill. */
  .content-holder .fields-wrapper .input-box > button:not(.icon-holder) {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    width: auto;
    max-width: none;
    height: 36px;
    padding: 0 14px;
    border: 1px dashed var(--offer-primary-line);
    border-radius: 14px;
    font-size: 13px;
    font-weight: 600;
    color: var(--hd-primary);
    background: var(--offer-primary-soft);
    transition: background 0.15s ease;

    &:hover {
      border-style: solid;
      background: color-mix(in oklab, var(--hd-primary) 12%, transparent);
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }

  /* Row remove buttons. */
  .content-holder .fields-wrapper .icon-holder {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.15s ease;

    &.delete {
      color: var(--hd-destructive);
      background: color-mix(in srgb, var(--hd-destructive) 10%, transparent);

      &:hover {
        background: color-mix(in srgb, var(--hd-destructive) 16%, transparent);
      }
    }

    &.edit {
      color: ${FG};
      background: ${MUTED};
    }
  }

  .content-holder .fields-wrapper .btn-wrapper .delete-btn {
    height: 32px;
    padding: 0 12px;
    border: 1px solid color-mix(in srgb, var(--hd-destructive) 35%, transparent);
    border-radius: 12px;
    font-size: 13px;
    color: var(--hd-destructive);
    background: ${BG};
  }

  /* Email length: two choice cards, the chosen one in the primary colour. */
  .content-holder .fields-wrapper .tabs-btns {
    gap: 12px;

    .tabBtn {
      padding: 0;
      border: 1px solid ${BORDER};
      border-radius: 18px;
      background: ${BG};
      cursor: pointer;
      transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease,
        background 0.15s ease;

      &:hover {
        border-color: color-mix(in srgb, var(--hd-foreground) 20%, transparent);
      }

      .btn-content {
        padding: 16px;
        border-radius: 18px;
      }

      .text-box .heading {
        margin: 0 0 2px;
        font-size: 18px;
        line-height: 26px;
        color: ${FG};
      }

      .text-box .subtitle {
        font-size: 13px;
        color: ${MUTED_FG};
      }

      &.active {
        border-color: var(--hd-primary);
        background: color-mix(in oklab, var(--hd-primary) 5%, transparent);
        box-shadow: 0 0 0 3px color-mix(in oklab, var(--hd-primary) 15%, transparent);

        .text-box .heading,
        .text-box .subtitle {
          color: var(--hd-primary);
        }

        .ico-tick {
          border-color: var(--hd-primary);
        }
      }
    }

    .ico-tick {
      border: 1.5px solid ${BORDER};

      &::before {
        background: var(--hd-primary);
      }
    }
  }

  /* Personalization options. */
  .content-holder .fields-wrapper .personalization-row {
    gap: 16px;
    padding: 14px 16px;
    border-radius: 18px;
  }

  .content-holder .fields-wrapper .icon-box {
    width: 40px;
    height: 40px;
    border-radius: 14px;
    color: var(--hd-primary);
    background: var(--offer-primary-soft);
  }

  .content-holder .fields-wrapper .text-box .heading {
    margin: 0 0 2px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    color: ${FG};
  }

  .content-holder .fields-wrapper .text-box .subtitle {
    font-size: 12px;
    line-height: 16px;
    color: ${MUTED_FG};
  }

  /* The shared Switch is on in the app's old blue. */
  .content-holder .fields-wrapper input[type='checkbox']:checked + .switch {
    background: var(--hd-primary);
  }

  /* version3: mt-6 flex justify-end gap-2, Cancel and Save. */
  .content-holder .btn-holder {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 24px;
  }

  .content-holder .btn-holder button {
    width: auto;
    min-width: 0;
    height: 40px;
    padding: 0 18px;
    border-radius: 14px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
  }

  .content-holder .btn-holder button[type='button'] {
    border: 1px solid ${BORDER};
    color: ${FG};
    background: ${BG};
    transition: background 0.15s ease;

    &:hover {
      background: ${MUTED};
    }
  }

  .content-holder .btn-holder button[type='submit'] {
    border: none;
    color: #fff;
    background: var(--hd-gradient-houdiny);
    box-shadow: var(--hd-shadow-card);
    transition: opacity 0.15s ease;

    &:hover:not(:disabled) {
      opacity: 0.9;
    }

    &:disabled {
      opacity: 0.6;
    }
  }
`;

export const StyledOfferSection = styled.section`
  margin-top: 24px;
  padding: 20px;
  border: 1px solid ${BORDER};
  border-radius: 22px;
  background: ${BG};

  .section-title {
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    color: ${FG};
  }

  .section-body {
    margin-top: 8px;
    font-size: 14px;
    line-height: 20px;
    white-space: pre-wrap;
    color: ${MUTED_FG};
  }

  .section-empty {
    margin-top: 8px;
    font-size: 14px;
    line-height: 20px;
    font-style: italic;
    color: ${MUTED_FG};
  }

  /* List-valued fields (proof points, outcomes, CTAs...) have no counterpart in
     the reference, which stores flat text. They keep the same card, with each
     entry on its own line. */
  .section-list {
    margin: 8px 0 0;
    padding: 0;
    list-style: none;

    li {
      position: relative;
      padding-left: 18px;
      font-size: 14px;
      line-height: 20px;
      color: ${MUTED_FG};

      & + li {
        margin-top: 6px;
      }

      &::before {
        content: '';
        position: absolute;
        top: 10px;
        left: 4px;
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: ${BORDER};
      }
    }
  }

  /* Entries stored as HTML by SmallEditor, which wraps each line in a <p>, so
     those lose their default margins and the row spacing is set here. */
  .section-rich {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 8px;
    font-size: 14px;
    line-height: 20px;
    color: ${MUTED_FG};

    p {
      margin: 0;
    }

    a {
      color: var(--hd-primary);
      text-decoration: underline;
      word-break: break-word;
    }
  }

  /* Pain point to solution pairs. */
  .section-pairs {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 8px;
  }

  .section-pair {
    padding: 12px;
    border-radius: 18px;
    background: ${MUTED_30};

    .pair-label {
      font-size: 11px;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: ${MUTED_FG};
    }

    .pair-value {
      margin-top: 2px;
      font-size: 14px;
      line-height: 20px;
      color: ${FG};
    }

    .pair-value + .pair-label {
      margin-top: 10px;
    }
  }

  /* Personalization tokens read as a row of chips. */
  .section-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  }

  .section-chip {
    display: inline-flex;
    align-items: center;
    height: 26px;
    padding: 0 10px;
    border: 1px solid ${BORDER};
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    color: ${FG};
    background: ${MUTED_30};
  }
`;
